package com.evoapps.evoshift.domain.schedule

import com.evoapps.evoshift.domain.model.Absence
import com.evoapps.evoshift.domain.model.AbsenceStatus
import com.evoapps.evoshift.domain.model.Availability
import com.evoapps.evoshift.domain.model.CompensationModel
import com.evoapps.evoshift.domain.model.Competence
import com.evoapps.evoshift.domain.model.FinancialEngagement
import com.evoapps.evoshift.domain.model.ShiftLeadRule
import com.evoapps.evoshift.domain.model.SystemContractType
import com.evoapps.evoshift.domain.model.StaffReadiness
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.model.UserPermission
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus

/**
 * Who needs supervision beyond the engagement's competence (SH-47): what workplaces used before
 * it, skills with these names. Who leads shifts is the workplace's [ShiftLeadRule] (SH-104).
 */
data class StaffRules(
    /** Any of these skills means the person needs supervision (case-insensitive); never a lead. */
    val supervisionSkills: Set<String> = setOf("Wymaga nadzoru", "Needs supervision")
)

/** Builds the solver's [StaffProfile]s from what the app stores today. */
object StaffProfiles {

    /**
     * The person's hours target for the month:
     * - an employment contract (umowa o pracę) with a monthly salary: FTE × the month's nominal
     *   working hours ([nominalHours], PayrollRules.workingHours: Polish holidays included);
     * - anything else: the declared hours (FinancialEngagement.declaredNumberOfHours);
     * - unknown when no hours were declared, there are no pay terms, or they cannot be read.
     * A target of 0 means "do not schedule".
     */
    fun monthlyTarget(pay: FinancialEngagement?, payTermsReadable: Boolean, nominalHours: Int): HoursTarget {
        if (!payTermsReadable) return HoursTarget.unknown(TargetSource.PAY_TERMS_UNREADABLE)
        if (pay == null) return HoursTarget.unknown(TargetSource.NO_PAY_TERMS)
        val salary = pay.compensation as? CompensationModel.MonthlyFixed
        if (pay.contract == SystemContractType.STANDARD_EMPLOYMENT && salary != null) {
            return HoursTarget.of(salary.fte * nominalHours, TargetSource.FULL_TIME_SHARE)
        }
        return pay.declaredNumberOfHours?.let { HoursTarget.of(it.toDouble(), TargetSource.DECLARED_HOURS) }
            ?: HoursTarget.unknown(TargetSource.NOT_DECLARED)
    }

    /**
     * The solver's view of [user] at [workplaceId] for the roster month [year]-[month].
     * [availability] is the person's month (null: none submitted); [absences] may include other
     * people's and unapproved ones (only their own approved ones count).
     */
    fun build(
        user: User,
        workplaceId: String,
        workplaceOwnerId: String?,
        year: Int,
        month: Int,
        target: HoursTarget,
        contract: SystemContractType,
        availability: Availability?,
        absences: List<Absence>,
        rules: StaffRules = StaffRules(),
        /** SH-104: who leads shifts here (ShiftLeadRule.of(workplace)); the owner always does. */
        leadRule: ShiftLeadRule = ShiftLeadRule(ownerId = workplaceOwnerId),
        constraints: List<StaffConstraint> = emptyList(),
        readiness: StaffReadiness? = null
    ): StaffProfile {
        val engagement = user.engagements[workplaceId]
        val skills = engagement?.skills.orEmpty().toSet()
        val permission = engagement?.permission ?: UserPermission.EMPLOYEE
        val isOwner = permission == UserPermission.OWNER || user.uid == workplaceOwnerId
        fun hasAny(names: Set<String>) = skills.any { skill -> names.any { it.equals(skill.trim(), ignoreCase = true) } }
        val isLead = isOwner || leadRule.leads(engagement, user.uid)
        val monthPrefix = "$year-${month.toString().padStart(2, '0')}"
        val days = availability?.days.orEmpty().entries
            .filter { (day, _) -> day in 1..31 }
            .associate { (day, preference) -> "$monthPrefix-${day.toString().padStart(2, '0')}" to preference }
        val notes = days.entries.sortedBy { it.key }.mapNotNull { (date, preference) ->
            preference.comment?.takeIf { it.isNotBlank() }?.let { "$date: ${it.trim()}" }
        }
        return StaffProfile(
            userId = user.uid,
            displayName = user.displayName,
            role = engagement?.role,
            skills = skills,
            isOwner = isOwner,
            leadsShifts = isLead,
            needsSupervision = !isLead && (engagement?.competence == Competence.NEEDS_SUPERVISION || hasAny(rules.supervisionSkills)),
            contract = contract,
            target = target,
            availability = days,
            availabilitySubmitted = availability != null && days.isNotEmpty(),
            absentDates = absentDates(user.uid, absences, year, month),
            staffChecksValidThrough = checksValidThrough(readiness),
            constraints = constraints,
            notes = notes
        )
    }

    /** Before any roster day: a check known only as EXPIRED blocks the whole month. */
    private const val BEFORE_ANY_DAY = "0000-01-01"

    /**
     * SH-47: the last day the person's medical exam and OHS training both cover, as an ISO date.
     * With the dates (owners, financial managers): the earlier "valid until". From the statuses
     * alone (operational managers): an EXPIRED check blocks every day, anything else is not known
     * to run out (like AssignUserToShiftUseCase's check). A missing date blocks nothing.
     */
    fun checksValidThrough(readiness: StaffReadiness?): String? {
        readiness ?: return null
        readiness.dates?.let { dates ->
            return listOfNotNull(dates.medicalExamValidUntil, dates.ohsValidUntil).minOrNull()?.toString()
        }
        return if (readiness.anyExpired) BEFORE_ANY_DAY else null
    }

    /** ISO dates of the month covered by the person's approved absences. */
    fun absentDates(userId: String, absences: List<Absence>, year: Int, month: Int): Set<String> {
        val first = LocalDate(year, month, 1)
        val last = first.plus(DatePeriod(months = 1)).plus(DatePeriod(days = -1))
        return absences.filter { it.userId == userId && it.status == AbsenceStatus.APPROVED }.flatMap { absence ->
            val start = runCatching { LocalDate.parse(absence.startDate.take(10)) }.getOrNull() ?: return@flatMap emptyList()
            val end = runCatching { LocalDate.parse(absence.endDate.take(10)) }.getOrNull() ?: return@flatMap emptyList()
            generateSequence(maxOf(start, first)) { it.plus(DatePeriod(days = 1)) }
                .takeWhile { it <= minOf(end, last) }
                .map { it.toString() }
                .toList()
        }.toSet()
    }
}
